import {
  AdditiveBlending,
  AmbientLight,
  Clock,
  Color,
  DirectionalLight,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  SRGBColorSpace,
  TorusGeometry,
  Vector2,
  WebGLRenderer,
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

export type OrbState = "idle" | "listening" | "thinking" | "speaking" | "error";
export type OrbMode = "tap" | "ptt";

const STATE_LABELS: Record<OrbState, string> = {
  idle: "Tippe auf den Orb, um zu sprechen",
  listening: "Ich höre …",
  thinking: "Denke nach …",
  speaking: "JARVIS spricht …",
  error: "Etwas ist schiefgelaufen",
};

const STATE_LABELS_PTT: Record<OrbState, string> = {
  idle: "Halte den Orb gedrückt zum Sprechen",
  listening: "Halten und sprechen …",
  thinking: "Denke nach …",
  speaking: "JARVIS spricht …",
  error: "Etwas ist schiefgelaufen",
};

const SPEAKER_MODE_SPEAKING_LABEL = "JARVIS spricht — Mikro pausiert";

export interface OrbOptions {
  onTap?(): void;
  onPressStart?(): void;
  onPressEnd?(): void;
  onPressCancel?(): void;
}

interface OrbUniforms {
  uTime: { value: number };
  uLevel: { value: number };
  uBreath: { value: number };
  uListen: { value: number };
  uThink: { value: number };
  uSpeak: { value: number };
  uError: { value: number };
  uTintA: { value: Color };
  uTintB: { value: Color };
}

interface OrbTarget {
  breath: number;
  listen: number;
  think: number;
  speak: number;
  error: number;
  spin: number;
  pulse: number;
  bloom: number;
}

const VERT = /* glsl */ `
uniform float uTime;
uniform float uLevel;
uniform float uBreath;
uniform float uListen;
uniform float uThink;
uniform float uSpeak;
uniform float uError;

varying vec3 vNormalW;
varying vec3 vWorldPos;
varying float vWave;

float hash(vec3 p) {
  p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
  p *= 17.0;
  return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
}

float noise(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);

  float n000 = hash(i + vec3(0.0, 0.0, 0.0));
  float n100 = hash(i + vec3(1.0, 0.0, 0.0));
  float n010 = hash(i + vec3(0.0, 1.0, 0.0));
  float n110 = hash(i + vec3(1.0, 1.0, 0.0));
  float n001 = hash(i + vec3(0.0, 0.0, 1.0));
  float n101 = hash(i + vec3(1.0, 0.0, 1.0));
  float n011 = hash(i + vec3(0.0, 1.0, 1.0));
  float n111 = hash(i + vec3(1.0, 1.0, 1.0));

  float nx00 = mix(n000, n100, f.x);
  float nx10 = mix(n010, n110, f.x);
  float nx01 = mix(n001, n101, f.x);
  float nx11 = mix(n011, n111, f.x);
  float nxy0 = mix(nx00, nx10, f.y);
  float nxy1 = mix(nx01, nx11, f.y);

  return mix(nxy0, nxy1, f.z) * 2.0 - 1.0;
}

float fbm(vec3 p) {
  float s = 0.0;
  float amp = 0.55;
  mat3 m = mat3(
    1.6, 1.2, 1.1,
    -1.2, 1.4, 0.9,
    1.0, -0.8, 1.7
  );
  for (int i = 0; i < 4; i++) {
    s += amp * noise(p);
    p = m * p;
    amp *= 0.5;
  }
  return s;
}

void main() {
  vec3 n = normalize(normal);
  float t = uTime;

  float breathWave = sin(t * 1.35 + n.y * 6.0) * 0.5 + 0.5;
  float flowNoise = fbm(n * (2.8 + uListen * 1.8 + uSpeak * 2.2) + vec3(0.0, t * 0.9, t * 0.55));
  float speakWave = sin((n.x + n.y + n.z) * 18.0 + t * (8.5 + uSpeak * 4.0)) * 0.5 + 0.5;
  float thinkPulse = sin(t * 4.2 + n.x * 5.0 - n.z * 4.0) * 0.5 + 0.5;

  float amp = 0.016;
  amp += uBreath * (0.020 + 0.010 * breathWave);
  amp += uListen * (0.036 + 0.022 * abs(flowNoise)) * (0.35 + uLevel * 0.9);
  amp += uThink * (0.024 + thinkPulse * 0.020);
  amp += uSpeak * (0.050 + speakWave * 0.050) * (0.45 + uLevel * 1.05);
  amp += uError * 0.030 * abs(sin(t * 16.0 + n.y * 16.0));

  vec3 displaced = position + n * amp;

  vec4 world = modelMatrix * vec4(displaced, 1.0);
  vec4 clip = projectionMatrix * viewMatrix * world;

  vNormalW = normalize(mat3(modelMatrix) * n);
  vWorldPos = world.xyz;
  vWave = amp;

  gl_Position = clip;
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform float uLevel;
uniform float uBreath;
uniform float uListen;
uniform float uThink;
uniform float uSpeak;
uniform float uError;
uniform vec3 uTintA;
uniform vec3 uTintB;

varying vec3 vNormalW;
varying vec3 vWorldPos;
varying float vWave;

void main() {
  vec3 n = normalize(vNormalW);
  vec3 v = normalize(cameraPosition - vWorldPos);
  vec3 r = reflect(-v, n);

  float horizon = clamp(1.0 - abs(r.y), 0.0, 1.0);
  float fresnel = pow(1.0 - max(dot(v, n), 0.0), 4.0);

  vec3 chromeDark = vec3(0.15, 0.19, 0.25);
  vec3 chromeMid = vec3(0.67, 0.72, 0.80);
  vec3 chromeHot = vec3(0.96, 0.97, 1.0);

  float stripe = sin(r.x * 18.0 + uTime * 1.3) * 0.5 + 0.5;
  float sweep = sin((r.z + r.y) * 10.0 - uTime * 0.8) * 0.5 + 0.5;

  vec3 baseChrome = mix(chromeDark, chromeMid, pow(horizon, 0.8));
  baseChrome = mix(baseChrome, chromeHot, pow(stripe, 8.0) * 0.9);
  baseChrome = mix(baseChrome, chromeHot, pow(sweep, 22.0) * 0.4);

  float auroraBand = sin(r.x * 8.0 - r.z * 7.0 + uTime * (0.9 + uThink * 0.5)) * 0.5 + 0.5;
  float auroraStrength = 0.12 + uListen * 0.20 + uSpeak * 0.24 + uThink * 0.18;
  vec3 aurora = mix(uTintA, uTintB, auroraBand) * auroraStrength;

  float detailGlow = clamp(vWave * 14.0, 0.0, 1.0);
  vec3 stateBoost = vec3(0.0);
  stateBoost += uSpeak * vec3(0.07, 0.05, 0.02);
  stateBoost += uThink * vec3(0.03, 0.03, 0.06);
  stateBoost += uError * vec3(0.55, 0.10, 0.10);

  vec3 col = baseChrome + aurora;
  col += detailGlow * (0.04 + uLevel * 0.06);
  col += fresnel * (0.28 + uListen * 0.18 + uSpeak * 0.22);
  col += stateBoost;

  gl_FragColor = vec4(col, 1.0);
}
`;

const GLOW_VERT = /* glsl */ `
varying float vFres;
void main() {
  vec3 n = normalize(normalMatrix * normal);
  vec3 v = normalize((modelViewMatrix * vec4(position, 1.0)).xyz);
  vFres = pow(1.0 - abs(dot(n, v)), 2.8);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const GLOW_FRAG = /* glsl */ `
uniform vec3 uGlowA;
uniform vec3 uGlowB;
uniform float uGlowStrength;
varying float vFres;
void main() {
  vec3 c = mix(uGlowA, uGlowB, clamp(vFres * 1.2, 0.0, 1.0));
  float a = vFres * uGlowStrength;
  gl_FragColor = vec4(c, a);
}
`;

export class Orb {
  readonly root: HTMLElement;

  private statusEl: HTMLElement;
  private buttonEl: HTMLButtonElement;
  private stageEl: HTMLElement;
  private viewportEl: HTMLElement;

  private state: OrbState = "idle";
  private mode: OrbMode = "tap";
  private targetLevel = 0;
  private displayLevel = 0;
  private customStatus: string | null = null;
  private opts: OrbOptions;
  private speakerMode = false;
  private reducedMotion = false;

  private isPressing = false;
  private activePointerId: number | null = null;

  private renderer: WebGLRenderer | null = null;
  private scene: Scene | null = null;
  private camera: PerspectiveCamera | null = null;
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private orbGroup: Group | null = null;
  private orbMesh: Mesh | null = null;
  private ringMesh: Mesh | null = null;
  private glowMesh: Mesh | null = null;

  private orbUniforms: OrbUniforms | null = null;
  private glowUniforms: { uGlowA: { value: Color }; uGlowB: { value: Color }; uGlowStrength: { value: number } } | null = null;

  private clock = new Clock();
  private rafId = 0;
  private resizeObserver: ResizeObserver | null = null;

  private stageW = 0;
  private stageH = 0;
  private motion = {
    breath: 0,
    listen: 0,
    think: 0,
    speak: 0,
    error: 0,
    spin: 0,
    pulse: 0,
    bloom: 0.65,
  };

  constructor(parent: HTMLElement, opts: OrbOptions = {}) {
    this.opts = opts;
    this.reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

    const wrap = document.createElement("div");
    wrap.className = "orb-wrap";
    wrap.dataset.state = "idle";
    wrap.innerHTML = `
      <div class="orb-stage" data-state="idle">
        <button type="button" class="orb" data-state="idle" data-mode="tap" aria-label="Sprechen">
          <div class="orb-wave-canvas" aria-hidden="true"></div>
        </button>
      </div>
      <p class="orb-status" aria-live="polite">${STATE_LABELS.idle}</p>
    `;
    parent.appendChild(wrap);

    this.root = wrap;
    this.stageEl = wrap.querySelector(".orb-stage") as HTMLElement;
    this.buttonEl = wrap.querySelector(".orb") as HTMLButtonElement;
    this.statusEl = wrap.querySelector(".orb-status") as HTMLElement;
    this.viewportEl = wrap.querySelector(".orb-wave-canvas") as HTMLElement;

    this.wireTap();
    this.wirePointer();
    this.initThree();
    this.wireResize();
    this.startLoop();
  }

  setMode(mode: OrbMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.buttonEl.dataset.mode = mode;
    if (this.customStatus === null) {
      this.statusEl.textContent = this.labelFor(this.state);
    }
    this.buttonEl.setAttribute("aria-label", mode === "ptt" ? "Halten zum Sprechen" : "Sprechen");
  }

  getMode(): OrbMode {
    return this.mode;
  }

  setState(state: OrbState): void {
    if (this.state === state) return;
    this.state = state;
    this.buttonEl.dataset.state = state;
    this.stageEl.dataset.state = state;
    this.root.dataset.state = state;
    if (state !== "listening" && state !== "speaking") {
      this.targetLevel = 0;
    }
    if (this.customStatus === null) {
      this.statusEl.textContent = this.labelFor(state);
    }
  }

  getState(): OrbState {
    return this.state;
  }

  setStatus(text: string | null): void {
    this.customStatus = text;
    this.statusEl.textContent = text ?? this.labelFor(this.state);
  }

  setLevel(rms: number): void {
    const normalized = Math.min(1, Math.max(0, rms * 8));
    this.targetLevel = normalized;
  }

  setSpeakerMode(enabled: boolean): void {
    if (this.speakerMode === enabled) return;
    this.speakerMode = enabled;
    if (this.customStatus === null) {
      this.statusEl.textContent = this.labelFor(this.state);
    }
  }

  destroy(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.disposeThree();
    this.root.remove();
  }

  private labelFor(state: OrbState): string {
    if (state === "speaking" && this.speakerMode) {
      return SPEAKER_MODE_SPEAKING_LABEL;
    }
    return this.mode === "ptt" ? STATE_LABELS_PTT[state] : STATE_LABELS[state];
  }

  private wireTap(): void {
    this.buttonEl.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.mode !== "tap") return;
      this.opts.onTap?.();
    });
  }

  private wirePointer(): void {
    const onDown = (e: PointerEvent) => {
      if (this.mode !== "ptt") return;
      if (this.isPressing) return;
      this.isPressing = true;
      this.activePointerId = e.pointerId;
      try {
        this.buttonEl.setPointerCapture(e.pointerId);
      } catch {
        // noop
      }
      this.buttonEl.classList.add("is-pressing");
      e.preventDefault();
      this.opts.onPressStart?.();
    };

    const onUp = (e: PointerEvent) => {
      if (this.mode !== "ptt") return;
      if (!this.isPressing) return;
      if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
      this.isPressing = false;
      this.activePointerId = null;
      try {
        this.buttonEl.releasePointerCapture(e.pointerId);
      } catch {
        // noop
      }
      this.buttonEl.classList.remove("is-pressing");
      this.opts.onPressEnd?.();
    };

    const onCancel = (e: PointerEvent) => {
      if (this.mode !== "ptt") return;
      if (!this.isPressing) return;
      if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
      this.isPressing = false;
      this.activePointerId = null;
      this.buttonEl.classList.remove("is-pressing");
      this.opts.onPressCancel?.();
    };

    this.buttonEl.addEventListener("pointerdown", onDown);
    this.buttonEl.addEventListener("pointerup", onUp);
    this.buttonEl.addEventListener("pointercancel", onCancel);
    this.buttonEl.addEventListener("pointerleave", (e) => {
      if (this.mode !== "ptt") return;
      if (!this.isPressing) return;
      if (!this.buttonEl.hasPointerCapture?.(e.pointerId)) onUp(e);
    });
  }

  private initThree(): void {
    const scene = new Scene();
    const camera = new PerspectiveCamera(34, 1, 0.1, 20);
    camera.position.set(0, 0, 4.25);

    const renderer = new WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = SRGBColorSpace;

    const renderPass = new RenderPass(scene, camera);
    const bloom = new UnrealBloomPass(new Vector2(1, 1), 0.92, 0.82, 0.12);
    bloom.threshold = 0.16;

    const composer = new EffectComposer(renderer);
    composer.addPass(renderPass);
    composer.addPass(bloom);

    const uniforms: OrbUniforms = {
      uTime: { value: 0 },
      uLevel: { value: 0 },
      uBreath: { value: 1 },
      uListen: { value: 0 },
      uThink: { value: 0 },
      uSpeak: { value: 0 },
      uError: { value: 0 },
      uTintA: { value: new Color("#00c875") },
      uTintB: { value: new Color("#00b4d8") },
    };

    const orbMat = new ShaderMaterial({
      uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
    });

    const orbGeom = new IcosahedronGeometry(1.0, this.reducedMotion ? 4 : 5);
    const orbMesh = new Mesh(orbGeom, orbMat);

    const glowUniforms = {
      uGlowA: { value: new Color("#89e6ff") },
      uGlowB: { value: new Color("#9fffd0") },
      uGlowStrength: { value: 0.36 },
    };
    const glowMesh = new Mesh(
      new IcosahedronGeometry(1.16, 4),
      new ShaderMaterial({
        uniforms: glowUniforms,
        vertexShader: GLOW_VERT,
        fragmentShader: GLOW_FRAG,
        transparent: true,
        depthWrite: false,
        blending: AdditiveBlending,
      }),
    );

    const ringMesh = new Mesh(
      new TorusGeometry(1.65, 0.015, 16, 140),
      new MeshBasicMaterial({
        color: new Color("#6fe0ff"),
        transparent: true,
        opacity: 0.28,
      }),
    );
    ringMesh.rotation.x = 0.95;
    ringMesh.rotation.y = -0.35;

    const group = new Group();
    group.add(glowMesh);
    group.add(orbMesh);
    group.add(ringMesh);

    scene.add(group);
    scene.add(new AmbientLight(0xffffff, 0.36));
    const key = new DirectionalLight(0xd7f3ff, 1.05);
    key.position.set(2.5, 3.0, 3.2);
    scene.add(key);
    const rim = new DirectionalLight(0x8ef4dd, 0.65);
    rim.position.set(-2.8, -1.2, 2.2);
    scene.add(rim);

    renderer.domElement.className = "orb-webgl";
    renderer.domElement.setAttribute("aria-hidden", "true");
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    renderer.domElement.style.pointerEvents = "none";
    this.viewportEl.appendChild(renderer.domElement);

    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    this.composer = composer;
    this.bloom = bloom;
    this.orbGroup = group;
    this.orbMesh = orbMesh;
    this.glowMesh = glowMesh;
    this.ringMesh = ringMesh;
    this.orbUniforms = uniforms;
    this.glowUniforms = glowUniforms;
  }

  private disposeThree(): void {
    this.composer?.dispose();

    if (this.orbMesh) {
      this.orbMesh.geometry.dispose();
      const mat = this.orbMesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }
    if (this.glowMesh) {
      this.glowMesh.geometry.dispose();
      const mat = this.glowMesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }
    if (this.ringMesh) {
      this.ringMesh.geometry.dispose();
      const mat = this.ringMesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }

    this.renderer?.dispose();
    if (this.renderer?.domElement.parentElement) {
      this.renderer.domElement.parentElement.removeChild(this.renderer.domElement);
    }

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.composer = null;
    this.bloom = null;
    this.orbGroup = null;
    this.orbMesh = null;
    this.glowMesh = null;
    this.ringMesh = null;
    this.orbUniforms = null;
    this.glowUniforms = null;
  }

  private wireResize(): void {
    const resize = () => this.applySize();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(resize);
      this.resizeObserver.observe(this.stageEl);
    } else {
      window.addEventListener("resize", resize);
    }
    requestAnimationFrame(resize);
  }

  private applySize(): void {
    if (!this.renderer || !this.composer || !this.camera) return;

    const rect = this.stageEl.getBoundingClientRect();
    const w = Math.max(160, Math.round(rect.width));
    const h = Math.max(160, Math.round(rect.height));
    this.stageW = w;
    this.stageH = h;

    const dpr = Math.min(window.devicePixelRatio || 1, this.reducedMotion ? 1.25 : 1.8);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    this.composer.setSize(w, h);

    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private targetForState(state: OrbState): OrbTarget {
    switch (state) {
      case "listening":
        return { breath: 0.85, listen: 1, think: 0, speak: 0, error: 0, spin: 0.25, pulse: 0.65, bloom: 1.05 };
      case "thinking":
        return { breath: 0.35, listen: 0.2, think: 1, speak: 0, error: 0, spin: 0.7, pulse: 0.8, bloom: 0.96 };
      case "speaking":
        return { breath: 0.15, listen: 0.2, think: 0.1, speak: 1, error: 0, spin: 0.48, pulse: 1, bloom: 1.22 };
      case "error":
        return { breath: 0.2, listen: 0, think: 0, speak: 0, error: 1, spin: 0.3, pulse: 0.4, bloom: 0.66 };
      case "idle":
      default:
        return { breath: 1, listen: 0, think: 0, speak: 0, error: 0, spin: 0.16, pulse: 0.28, bloom: 0.72 };
    }
  }

  private startLoop(): void {
    const tick = () => {
      this.rafId = requestAnimationFrame(tick);
      this.render();
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private render(): void {
    if (!this.orbUniforms || !this.orbGroup || !this.composer || !this.bloom || !this.glowUniforms) return;

    const dt = Math.min(0.05, this.clock.getDelta());
    const elapsed = this.clock.elapsedTime;

    const diff = this.targetLevel - this.displayLevel;
    this.displayLevel += diff * (diff > 0 ? 0.28 : 0.12);

    const target = this.targetForState(this.state);
    const lerpK = this.reducedMotion ? 0.08 : 0.12;
    this.motion.breath += (target.breath - this.motion.breath) * lerpK;
    this.motion.listen += (target.listen - this.motion.listen) * lerpK;
    this.motion.think += (target.think - this.motion.think) * lerpK;
    this.motion.speak += (target.speak - this.motion.speak) * lerpK;
    this.motion.error += (target.error - this.motion.error) * lerpK;
    this.motion.spin += (target.spin - this.motion.spin) * lerpK;
    this.motion.pulse += (target.pulse - this.motion.pulse) * lerpK;
    this.motion.bloom += (target.bloom - this.motion.bloom) * lerpK;

    const level = Math.max(0, Math.min(1, this.displayLevel));

    this.orbUniforms.uTime.value = elapsed;
    this.orbUniforms.uLevel.value = level;
    this.orbUniforms.uBreath.value = this.motion.breath;
    this.orbUniforms.uListen.value = this.motion.listen;
    this.orbUniforms.uThink.value = this.motion.think;
    this.orbUniforms.uSpeak.value = this.motion.speak;
    this.orbUniforms.uError.value = this.motion.error;

    if (this.state === "speaking") {
      this.orbUniforms.uTintA.value.set("#ffd3b2");
      this.orbUniforms.uTintB.value.set("#7fdcff");
    } else if (this.state === "thinking") {
      this.orbUniforms.uTintA.value.set("#c7b3ff");
      this.orbUniforms.uTintB.value.set("#98dfff");
    } else if (this.state === "error") {
      this.orbUniforms.uTintA.value.set("#ff8f8f");
      this.orbUniforms.uTintB.value.set("#ff6a6a");
    } else {
      this.orbUniforms.uTintA.value.set("#88ffd2");
      this.orbUniforms.uTintB.value.set("#7ad9ff");
    }

    this.orbGroup.rotation.y += dt * (0.2 + this.motion.spin * 1.4 + level * this.motion.speak * 0.9);
    this.orbGroup.rotation.x = Math.sin(elapsed * 0.7) * 0.08;

    if (this.ringMesh) {
      this.ringMesh.rotation.z += dt * (0.22 + this.motion.think * 0.9);
      const ringLevel = 0.22 + this.motion.think * 0.22 + this.motion.speak * 0.18;
      (this.ringMesh.material as MeshBasicMaterial).opacity = ringLevel;
    }

    if (this.glowUniforms) {
      this.glowUniforms.uGlowStrength.value =
        0.28 +
        this.motion.listen * 0.18 +
        this.motion.think * 0.12 +
        this.motion.speak * (0.18 + level * 0.2);
      if (this.state === "speaking") {
        this.glowUniforms.uGlowA.value.set("#ffbe84");
        this.glowUniforms.uGlowB.value.set("#75dfff");
      } else if (this.state === "thinking") {
        this.glowUniforms.uGlowA.value.set("#b9a3ff");
        this.glowUniforms.uGlowB.value.set("#8be7ff");
      } else if (this.state === "error") {
        this.glowUniforms.uGlowA.value.set("#ff8686");
        this.glowUniforms.uGlowB.value.set("#ff5d5d");
      } else {
        this.glowUniforms.uGlowA.value.set("#7ae8ff");
        this.glowUniforms.uGlowB.value.set("#9affd2");
      }
    }

    const stateBoost = this.motion.speak * (0.25 + level * 0.45) + this.motion.listen * 0.22 + this.motion.think * 0.2;
    this.bloom.strength = this.reducedMotion ? 0.45 : this.motion.bloom + stateBoost;
    this.bloom.radius = this.reducedMotion ? 0.5 : 0.78;

    this.composer.render();
  }
}
